import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ─────────────────────────────────────────────────────────────────

export type EscopoType = 'contrato' | 'portfolio';

export type DatasetKey =
  | 'obras'
  | 'cronograma'
  | 'medicoes'
  | 'financeiro'
  | 'nao_conformidades'
  | 'efetivo'
  | 'auditoria'
  | 'portfolio_resumo';

export interface ColumnDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean';
}

export interface DatasetDef {
  key: DatasetKey;
  label: string;
  icon: string;
  adminOnly?: boolean;
  portfolioOnly?: boolean;   // disponível apenas em escopo portfólio
  contratoOnly?: boolean;    // disponível apenas em escopo contrato
  columns: ColumnDef[];
}

export type FilterOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'ilike' | 'is';

export interface FilterClause {
  id: string;
  column: string;
  op: FilterOp;
  value: string;
}

export interface SortClause {
  column: string;
  dir: 'asc' | 'desc';
}

export interface ReportConfig {
  columns: string[];
  filters: FilterClause[];
  groupBy: string[];
  sortBy: SortClause[];
  pageSize: number;
}

export interface ReportTemplate {
  id: string;
  contrato_id: string;
  created_by: string;
  nome: string;
  descricao: string | null;
  dataset: DatasetKey;
  config: ReportConfig;
  visibilidade: 'privado' | 'equipe' | 'contrato';
  is_favorito: boolean;
  // Sprint 3.2 — campos de portfólio
  escopo: EscopoType;
  cluster_id: string | null;
  contrato_ids: string[] | null;
  created_at: string;
  updated_at: string;
}

// ─── Dataset catalog ────────────────────────────────────────────────────────

export const DATASET_CATALOG: DatasetDef[] = [
  {
    key: 'obras',
    label: 'Planilha de Obras',
    icon: '🏗️',
    contratoOnly: true,
    columns: [
      { key: 'codigo', label: 'Código', type: 'text' },
      { key: 'descricao', label: 'Descrição', type: 'text' },
      { key: 'unidade', label: 'Unidade', type: 'text' },
      { key: 'quantidade_contratada', label: 'Qtd. Contratada', type: 'number' },
      { key: 'preco_unitario', label: 'Preço Unitário', type: 'number' },
      { key: 'valor_total', label: 'Valor Total', type: 'number' },
      { key: 'quantidade_executada', label: 'Qtd. Executada', type: 'number' },
      { key: 'percentual_execucao', label: '% Execução', type: 'number' },
      { key: 'created_at', label: 'Criado em', type: 'date' },
    ],
  },
  {
    key: 'cronograma',
    label: 'Cronograma',
    icon: '📅',
    columns: [
      { key: 'codigo_wbs', label: 'Código WBS', type: 'text' },
      { key: 'nome', label: 'Atividade', type: 'text' },
      { key: 'tipo', label: 'Tipo', type: 'text' },
      { key: 'data_inicio_prevista', label: 'Início Previsto', type: 'date' },
      { key: 'data_fim_prevista', label: 'Fim Previsto', type: 'date' },
      { key: 'peso_fisico', label: 'Peso Físico', type: 'number' },
      { key: 'status', label: 'Status', type: 'text' },
    ],
  },
  {
    key: 'medicoes',
    label: 'Medições',
    icon: '📏',
    columns: [
      { key: 'numero_boletim', label: 'Nº Boletim', type: 'number' },
      { key: 'periodo_inicio', label: 'Período Início', type: 'date' },
      { key: 'periodo_fim', label: 'Período Fim', type: 'date' },
      { key: 'valor_bruto', label: 'Valor Bruto', type: 'number' },
      { key: 'valor_liquido', label: 'Valor Líquido', type: 'number' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'aprovado_em', label: 'Data Aprovação', type: 'date' },
      { key: 'observacoes', label: 'Observações', type: 'text' },
    ],
  },
  {
    key: 'financeiro',
    label: 'Financeiro / EVM',
    icon: '💰',
    columns: [
      { key: 'periodo_referencia', label: 'Período', type: 'date' },
      { key: 'pv', label: 'PV (Valor Planejado)', type: 'number' },
      { key: 'ev', label: 'EV (Valor Agregado)', type: 'number' },
      { key: 'ac', label: 'AC (Custo Real)', type: 'number' },
      { key: 'spi', label: 'SPI', type: 'number' },
      { key: 'cpi', label: 'CPI', type: 'number' },
      { key: 'eac', label: 'EAC (Estimativa Final)', type: 'number' },
      { key: 'sv', label: 'SV (Variação Prazo)', type: 'number' },
      { key: 'cv', label: 'CV (Variação Custo)', type: 'number' },
    ],
  },
  {
    key: 'nao_conformidades',
    label: 'Não Conformidades',
    icon: '⚠️',
    columns: [
      { key: 'descricao', label: 'Descrição', type: 'text' },
      { key: 'tipo', label: 'Tipo', type: 'text' },
      { key: 'severidade', label: 'Severidade', type: 'text' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'acao_corretiva', label: 'Ação Corretiva', type: 'text' },
      { key: 'responsavel_nome', label: 'Responsável', type: 'text' },
      { key: 'resolvido_em', label: 'Resolvido em', type: 'date' },
      { key: 'resolucao_descricao', label: 'Descrição Resolução', type: 'text' },
    ],
  },
  {
    key: 'efetivo',
    label: 'Efetivo Diário',
    icon: '👷',
    contratoOnly: true,
    columns: [
      { key: 'data', label: 'Data', type: 'date' },
      { key: 'funcao', label: 'Função', type: 'text' },
      { key: 'quantidade', label: 'Quantidade', type: 'number' },
      { key: 'empresa_subcontratada', label: 'Subcontratada', type: 'text' },
      { key: 'observacoes', label: 'Observações', type: 'text' },
    ],
  },
  {
    key: 'auditoria',
    label: 'Auditoria',
    icon: '🔍',
    adminOnly: true,
    contratoOnly: true,
    columns: [
      { key: 'acao', label: 'Ação', type: 'text' },
      { key: 'entidade', label: 'Entidade', type: 'text' },
      { key: 'entidade_id', label: 'ID Entidade', type: 'text' },
      { key: 'dados_resumo', label: 'Resumo', type: 'text' },
      { key: 'created_at', label: 'Data/Hora', type: 'date' },
    ],
  },
  {
    key: 'portfolio_resumo',
    label: 'Resumo de Portfólio',
    icon: '📊',
    portfolioOnly: true,
    columns: [
      { key: 'contrato_nome', label: 'Contrato', type: 'text' },
      { key: 'contrato_numero', label: 'Nº Contrato', type: 'text' },
      { key: 'cluster', label: 'Cluster', type: 'text' },
      { key: 'status_contrato', label: 'Status', type: 'text' },
      { key: 'spi_ultimo', label: 'SPI (último)', type: 'number' },
      { key: 'cpi_ultimo', label: 'CPI (último)', type: 'number' },
      { key: 'percentual_fisico', label: '% Físico', type: 'number' },
      { key: 'desvios_abertos', label: 'Desvios Abertos', type: 'number' },
      { key: 'glosas_total', label: 'Glosas Total', type: 'number' },
    ],
  },
];

export const OP_LABELS: Record<FilterOp, string> = {
  eq: '=', neq: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', ilike: 'contém', is: 'é nulo',
};

const DEFAULT_CONFIG: ReportConfig = {
  columns: [], filters: [], groupBy: [], sortBy: [], pageSize: 100,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useReportBuilder(contratoId: string) {
  const { toast } = useToast();
  const { isAdmin, isCoAdmin, user, role } = useAuth();
  const qc = useQueryClient();
  const previewDebounce = useRef<ReturnType<typeof setTimeout>>();

  // ── Scope state
  const [escopo, setEscopoState] = useState<EscopoType>('contrato');
  const [portfolioClusterId, setPortfolioClusterId] = useState<string | null>(null);
  const [portfolioContratoIds, setPortfolioContratoIds] = useState<string[]>([]);

  const [dataset, setDatasetState] = useState<DatasetKey>('obras');
  const [config, setConfig] = useState<ReportConfig>(DEFAULT_CONFIG);
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const isGestorOrAbove = isAdmin || isCoAdmin || role === 'gestor';

  // ── Available datasets filtered by role + escopo
  const availableDatasets = DATASET_CATALOG.filter(d => {
    if (d.adminOnly && !isAdmin && !isCoAdmin) return false;
    if (escopo === 'portfolio') return !d.contratoOnly;
    if (escopo === 'contrato') return !d.portfolioOnly;
    return true;
  });

  const currentDataset = DATASET_CATALOG.find(d => d.key === dataset) ?? DATASET_CATALOG[0];

  // ── Escopo change: reset dataset se necessário
  const setEscopo = useCallback((e: EscopoType) => {
    setEscopoState(e);
    setConfig(DEFAULT_CONFIG);
    // Se o dataset atual não é compatível com o novo escopo, seleciona o primeiro disponível
    const compatible = DATASET_CATALOG.filter(d => {
      if (d.adminOnly && !isAdmin && !isCoAdmin) return false;
      if (e === 'portfolio') return !d.contratoOnly;
      if (e === 'contrato') return !d.portfolioOnly;
      return true;
    });
    const first = compatible[0]?.key ?? 'obras';
    setDatasetState(first);
  }, [isAdmin, isCoAdmin]);

  // ── Dataset change → reset columns
  const setDataset = useCallback((key: DatasetKey) => {
    setDatasetState(key);
    setConfig(c => ({ ...c, columns: [], filters: [], sortBy: [], groupBy: [] }));
  }, []);

  // ── Column toggle
  const toggleColumn = useCallback((col: string) => {
    setConfig(c => ({
      ...c,
      columns: c.columns.includes(col) ? c.columns.filter(x => x !== col) : [...c.columns, col],
    }));
  }, []);

  // ── Filters
  const addFilter = useCallback(() => {
    setConfig(c => ({
      ...c,
      filters: [...c.filters, { id: crypto.randomUUID(), column: currentDataset.columns[0]?.key ?? '', op: 'eq', value: '' }],
    }));
  }, [currentDataset]);

  const updateFilter = useCallback((id: string, patch: Partial<FilterClause>) => {
    setConfig(c => ({ ...c, filters: c.filters.map(f => f.id === id ? { ...f, ...patch } : f) }));
  }, []);

  const removeFilter = useCallback((id: string) => {
    setConfig(c => ({ ...c, filters: c.filters.filter(f => f.id !== id) }));
  }, []);

  // ── Sort
  const toggleSort = useCallback((col: string) => {
    setConfig(c => {
      const existing = c.sortBy.find(s => s.column === col);
      if (!existing) return { ...c, sortBy: [...c.sortBy, { column: col, dir: 'asc' }] };
      if (existing.dir === 'asc') return { ...c, sortBy: c.sortBy.map(s => s.column === col ? { ...s, dir: 'desc' as const } : s) };
      return { ...c, sortBy: c.sortBy.filter(s => s.column !== col) };
    });
  }, []);

  // ── Validação de portfólio antes de preview/export
  const portfolioReady = escopo === 'portfolio'
    ? (portfolioClusterId !== null && portfolioContratoIds.length > 0)
    : true;

  // ── Preview
  const runPreview = useCallback(async (cfg: ReportConfig, ds: DatasetKey) => {
    if (cfg.columns.length === 0) { setPreviewRows([]); return; }
    if (!portfolioReady) { setPreviewError('Selecione cluster e ao menos um contrato para pré-visualizar.'); return; }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      if (escopo === 'portfolio') {
        const res = await supabase.functions.invoke('report-execute-portfolio', {
          body: {
            dataset: ds,
            contratoIds: portfolioContratoIds,
            clusterId: portfolioClusterId,
            columns: cfg.columns,
            filters: cfg.filters.filter(f => f.value !== '' || f.op === 'is'),
            sortBy: cfg.sortBy,
            pageSize: 50,
            preview: true,
          },
        });
        if (res.error) throw new Error(res.error.message);
        setPreviewRows(res.data?.data ?? []);
      } else {
        const res = await supabase.functions.invoke('report-execute', {
          body: {
            dataset: ds,
            contratoId,
            columns: cfg.columns,
            filters: cfg.filters.filter(f => f.value !== '' || f.op === 'is'),
            sortBy: cfg.sortBy,
            pageSize: 50,
            preview: true,
          },
        });
        if (res.error) throw new Error(res.error.message);
        setPreviewRows(res.data?.data ?? []);
      }
    } catch (err: unknown) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewLoading(false);
    }
  }, [contratoId, escopo, portfolioClusterId, portfolioContratoIds, portfolioReady]);

  const schedulePreview = useCallback((cfg: ReportConfig, ds: DatasetKey) => {
    clearTimeout(previewDebounce.current);
    previewDebounce.current = setTimeout(() => runPreview(cfg, ds), 600);
  }, [runPreview]);

  // ── Templates CRUD
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['emp-report-templates', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_report_templates' as never)
        .select('*')
        .eq('contrato_id', contratoId)
        .order('is_favorito', { ascending: false })
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ReportTemplate[];
    },
    enabled: !!contratoId,
  });

  const saveTemplate = useMutation({
    mutationFn: async (payload: { nome: string; descricao?: string; visibilidade: 'privado' | 'equipe' | 'contrato' }) => {
      const insertPayload: Record<string, unknown> = {
        contrato_id: contratoId,
        created_by: user!.id,
        nome: payload.nome,
        descricao: payload.descricao ?? null,
        dataset,
        config: config as unknown,
        visibilidade: payload.visibilidade,
        // Sprint 3.2
        escopo,
        cluster_id: escopo === 'portfolio' ? portfolioClusterId : null,
        contrato_ids: escopo === 'portfolio' ? portfolioContratoIds : null,
      };

      const { data, error } = await supabase
        .from('emp_report_templates' as never)
        .insert(insertPayload as never)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Relatório salvo', description: 'Template salvo com sucesso.' });
      qc.invalidateQueries({ queryKey: ['emp-report-templates', contratoId] });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Erro ao salvar', description: err.message });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('emp_report_templates' as never)
        .delete()
        .eq('id', id as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Template excluído' });
      qc.invalidateQueries({ queryKey: ['emp-report-templates', contratoId] });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Erro ao excluir', description: err.message });
    },
  });

  const toggleFavorito = useMutation({
    mutationFn: async ({ id, val }: { id: string; val: boolean }) => {
      const { error } = await supabase
        .from('emp_report_templates' as never)
        .update({ is_favorito: val } as never)
        .eq('id', id as never);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emp-report-templates', contratoId] }),
  });

  const loadTemplate = useCallback((t: ReportTemplate) => {
    // Restaurar escopo e campos de portfólio
    const templateEscopo = (t.escopo as EscopoType) ?? 'contrato';
    setEscopoState(templateEscopo);
    if (templateEscopo === 'portfolio') {
      setPortfolioClusterId(t.cluster_id ?? null);
      setPortfolioContratoIds(t.contrato_ids ?? []);
    } else {
      setPortfolioClusterId(null);
      setPortfolioContratoIds([]);
    }
    setDatasetState(t.dataset);
    setConfig(t.config);
    schedulePreview(t.config, t.dataset);
  }, [schedulePreview]);

  // ── Export
  const exportData = useCallback(async (format: 'excel' | 'csv') => {
    if (config.columns.length === 0) {
      toast({ variant: 'destructive', title: 'Selecione colunas antes de exportar' });
      return;
    }
    if (!portfolioReady && escopo === 'portfolio') {
      toast({ variant: 'destructive', title: 'Selecione cluster e contratos antes de exportar' });
      return;
    }

    try {
      let rows: Record<string, unknown>[] = [];
      let meta: Record<string, unknown> = {};

      if (escopo === 'portfolio') {
        const res = await supabase.functions.invoke('report-execute-portfolio', {
          body: {
            dataset,
            contratoIds: portfolioContratoIds,
            clusterId: portfolioClusterId,
            columns: config.columns,
            filters: config.filters.filter(f => f.value !== '' || f.op === 'is'),
            sortBy: config.sortBy,
            pageSize: 1000,
            preview: false,
          },
        });
        if (res.error) throw new Error(res.error.message);
        rows = res.data?.data ?? [];
        meta = res.data?.meta ?? {};
      } else {
        const res = await supabase.functions.invoke('report-execute', {
          body: {
            dataset,
            contratoId,
            columns: config.columns,
            filters: config.filters.filter(f => f.value !== '' || f.op === 'is'),
            sortBy: config.sortBy,
            pageSize: 1000,
            preview: false,
          },
        });
        if (res.error) throw new Error(res.error.message);
        rows = res.data?.data ?? [];
      }

      // Avisar sobre contratos excluídos
      if (meta?.contratos_excluidos && Array.isArray(meta.contratos_excluidos) && meta.contratos_excluidos.length > 0) {
        toast({
          title: 'Atenção: contratos excluídos',
          description: `${meta.contratos_excluidos.length} contrato(s) foram removidos por falta de permissão.`,
        });
      }

      if (format === 'excel') {
        const { writeExcel } = await import('@/lib/excelEngine');
        await writeExcel(
          [{ name: currentDataset.label.slice(0, 31), data: rows }],
          `relatorio-${dataset}-${escopo}`
        );
        toast({ title: 'Exportação concluída', description: `relatorio-${dataset}-${escopo}.xlsx baixado.` });
      } else {
        const headers = config.columns.join(',');
        const csvRows = rows.map((r) => config.columns.map(c => JSON.stringify(r[c] ?? '')).join(','));
        const csv = [headers, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `relatorio-${dataset}-${escopo}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'CSV exportado' });
      }
    } catch (err: unknown) {
      toast({ variant: 'destructive', title: 'Erro na exportação', description: err instanceof Error ? err.message : String(err) });
    }
  }, [config, dataset, contratoId, escopo, portfolioClusterId, portfolioContratoIds, portfolioReady, currentDataset, toast]);

  return {
    // scope state
    escopo, portfolioClusterId, portfolioContratoIds, isGestorOrAbove, portfolioReady,
    setEscopo, setPortfolioClusterId, setPortfolioContratoIds,
    // report state
    dataset, config, previewRows, previewLoading, previewError,
    templates, templatesLoading, availableDatasets, currentDataset,
    // actions
    setDataset, toggleColumn, addFilter, updateFilter, removeFilter, toggleSort,
    schedulePreview, setConfig,
    saveTemplate, deleteTemplate, toggleFavorito, loadTemplate, exportData,
  };
}
